import { Card, DEFAULT_DATE_FORMAT, FormattedDate } from "@dgs/core";
import { FC, ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { HotelStayFormValues, HotelSummaryResource } from "../../../shared/types/hotel";
import { SummaryValue } from "../../SummaryValue";
import { Value } from "../../Value";
import { ValueLabel } from "../../ValueLabel";
import { HotelSummaryRequestedNights } from "./HotelSummaryRequestedNights";
import { HotelStayListItemHeading } from "./HotestayList";

interface Props {
	summary: HotelSummaryResource;
	formValues: HotelStayFormValues | undefined;
	heading: ReactNode;
}

const Summary = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	grid-auto-rows: max-content;
	gap: ${({ theme }) => theme.spacing(4)};
	padding: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 1fr 1fr;
	}
`;

const SideBySide = styled(Card)`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};
	background: ${({ theme }) => theme.colors.palette.grey[10]};

	${Summary} {
		border: none;
		border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}

	${({ theme }) => theme.breakpoints.mobile} {
		grid-template-columns: 7fr 3fr;

		${Summary} {
			border: none;
			border-right: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
		}
	}
`;

export const HotelStaySummary: FC<Props> = ({ summary, formValues, heading }) => {
	const titleId = useId();
	const { t } = useTranslation();

	return (
		<li aria-labelledby={titleId}>
			<HotelStayListItemHeading id={titleId}>{heading}</HotelStayListItemHeading>
			<SideBySide $variant="outlined">
				<Summary>
					<SummaryValue>
						<ValueLabel>{t("Requested check-in")}:</ValueLabel>
						<Value
							value={<FormattedDate date={summary.checkIn} format={{ ...DEFAULT_DATE_FORMAT, weekday: "short" }} />}
						/>
					</SummaryValue>
					<SummaryValue>
						<ValueLabel>{t("Requested check-out")}:</ValueLabel>
						<Value
							value={<FormattedDate date={summary.checkOut} format={{ ...DEFAULT_DATE_FORMAT, weekday: "short" }} />}
						/>
					</SummaryValue>
					{summary.stayConfiguration.isPreferredHotelSelectable && (
						<SummaryValue>
							<ValueLabel>{t("Requested hotel")}:</ValueLabel>
							<Value value={summary.hotelName} />
						</SummaryValue>
					)}
					{summary.stayConfiguration.isPreferredRoomTypeSelectable && (
						<SummaryValue>
							<ValueLabel>{t("Requested room")}:</ValueLabel>
							<Value value={summary.roomTypeName} />
						</SummaryValue>
					)}
					{summary.stayConfiguration.allowRemarkByGuest && (
						<SummaryValue>
							<ValueLabel>{t("Remarks for stay")}:</ValueLabel>
							<Value value={formValues?.remarkByGuest} />
						</SummaryValue>
					)}
					{summary.stayConfiguration.allowAdditionalPersons && (
						<SummaryValue>
							<ValueLabel>{t("Additional person")}:</ValueLabel>
							<Value value={formValues?.additionalPersonsText} />
						</SummaryValue>
					)}
				</Summary>
				<HotelSummaryRequestedNights
					currency={summary.currency}
					prices={summary.prices}
					total={summary.totalPrice}
					addedAdditionalPersons={(formValues?.additionalPersonsText ?? "") !== ""}
				/>
			</SideBySide>
		</li>
	);
};
